/**
 * arEngine — WebGL AR try-on. Renders the webcam as a background and locks a 2D
 * glasses front-frame photo onto the face using MediaPipe's landmarks + head pose
 * matrix (position, scale, roll, gentle yaw). three.js is dynamically imported so
 * it lands in its own lazy chunk and never slows the main app.
 */

import type { NormLandmark, RawDetection } from './overlayEngine';

const L = {
  templeL: 234,
  templeR: 454,
  eyeOuterL: 33,
  eyeOuterR: 263,
  noseBridge: 168,
  faceOval: [
    10, 338, 297, 332, 284, 251, 389, 356, 454, 323, 361, 288, 397, 365, 379,
    378, 400, 377, 152, 148, 176, 149, 150, 136, 172, 58, 132, 93, 234, 127,
    162, 21, 54, 103, 67, 109,
  ],
};

type THREE = typeof import('three');

export interface AREngine {
  setPhotoFrame: (imageUrl: string, opacity?: number) => Promise<void>;
  clearModel: () => void;
  update: (det: RawDetection, videoW: number, videoH: number) => void;
  snapshot: () => string;
  resize: () => void;
  dispose: () => void;
}

function toWorld(lm: NormLandmark, aspect: number): [number, number, number] {
  return [(lm.x - 0.5) * 2 * aspect, -(lm.y - 0.5) * 2, -lm.z * 2];
}

function dist3(a: number[], b: number[]): number {
  return Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
}

/**
 * Ease-out-back curve — overshoots slightly past 1 then settles. Used to give a
 * newly applied frame a subtle spring "snap on" instead of popping into place.
 */
function easeOutBack(x: number): number {
  const c1 = 1.2; // tuned for a subtle, professional springiness
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(x - 1, 3) + c1 * Math.pow(x - 1, 2);
}

export async function createAREngine(
  canvas: HTMLCanvasElement,
  video: HTMLVideoElement
): Promise<AREngine> {
  const THREE: THREE = await import('three');

  const renderer = new THREE.WebGLRenderer({
    canvas,
    alpha: true,
    antialias: true,
    preserveDrawingBuffer: true,
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.outputColorSpace = THREE.SRGBColorSpace;

  const scene = new THREE.Scene();
  const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, -10, 10);
  camera.position.set(0, 0, 5);
  camera.lookAt(0, 0, 0);

  scene.add(new THREE.AmbientLight(0xffffff, 1.25)); // enhanced lighting for vibrancy
  const key = new THREE.DirectionalLight(0xffffff, 1.1);
  key.position.set(0.4, 0.8, 2);
  scene.add(key);
  const fill = new THREE.DirectionalLight(0xffffff, 0.5);
  fill.position.set(-0.6, 0.2, 1);
  scene.add(fill);

  // ── Video background ──
  const videoTex = new THREE.VideoTexture(video);
  videoTex.colorSpace = THREE.SRGBColorSpace;
  const bgScene = new THREE.Scene();
  const bgCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  const bgMesh = new THREE.Mesh(
    new THREE.PlaneGeometry(2, 2),
    new THREE.MeshBasicMaterial({ map: videoTex, depthTest: false, depthWrite: false })
  );
  bgMesh.scale.x = -1; // mirror (selfie)
  bgScene.add(bgMesh);

  // ── Depth-only face occluder ──
  const occluderGeom = new THREE.BufferGeometry();
  occluderGeom.setAttribute(
    'position',
    new THREE.BufferAttribute(new Float32Array(L.faceOval.length * 3), 3)
  );
  const fanIdx: number[] = [];
  for (let i = 1; i < L.faceOval.length - 1; i++) fanIdx.push(0, i, i + 1);
  occluderGeom.setIndex(fanIdx);
  const occluder = new THREE.Mesh(
    occluderGeom,
    new THREE.MeshBasicMaterial({ colorWrite: false })
  );
  occluder.renderOrder = 0;
  scene.add(occluder);

  // ── Glasses model holder ──
  const glassesGroup = new THREE.Group();
  glassesGroup.renderOrder = 1;
  glassesGroup.visible = false;
  scene.add(glassesGroup);

  let modelRoot: import('three').Object3D | null = null;
  let modelMaterial: import('three').MeshBasicMaterial | null = null;
  let modelAspect = 1.0;
  let modelBaseWidth = 1.0;
  const isPhotoFrame = true;

  // Frame-swap transition: 0 → 1 over ~20 frames, driving a fade-in plus an
  // ease-out-back scale so a newly selected frame springs onto the face.
  let transitionProgress = 1.0;
  let maxTargetOpacity = 1.0;

  async function setPhotoFrame(imageUrl: string, opacity = 1) {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    await new Promise<void>((res, rej) => {
      img.onload = () => res();
      img.onerror = () => rej(new Error('frame load failed'));
      img.src = imageUrl;
    });

    const { canvas: trimmed } = trimAndTint(img);
    const tex = new THREE.CanvasTexture(trimmed);
    tex.colorSpace = THREE.SRGBColorSpace;

    if (modelRoot) {
      glassesGroup.remove(modelRoot);
      modelRoot = null;
    }

    modelAspect = trimmed.height / trimmed.width;
    maxTargetOpacity = opacity;

    modelMaterial = new THREE.MeshBasicMaterial({
      map: tex,
      transparent: true,
      opacity: 0.0, // start invisible — the update loop fades it in
      side: THREE.DoubleSide,
      depthWrite: false,
    });
    const front = new THREE.Mesh(new THREE.PlaneGeometry(1, modelAspect), modelMaterial);
    front.scale.y = -1;

    modelBaseWidth = 1.0;
    modelRoot = front;
    glassesGroup.add(front);
    glassesGroup.visible = true;

    // Restart the fade-in / spring-scale transition for this frame.
    transitionProgress = 0.0;
  }

  function trimAndTint(img: HTMLImageElement): { canvas: HTMLCanvasElement; tint: number } {
    const fw = img.naturalWidth;
    const fh = img.naturalHeight;
    const work = document.createElement('canvas');
    work.width = fw;
    work.height = fh;
    const wctx = work.getContext('2d', { willReadFrequently: true });
    let x0 = 0, y0 = 0, x1 = fw, y1 = fh;
    let tint = 0x222222;
    if (wctx) {
      wctx.drawImage(img, 0, 0);
      try {
        const { data } = wctx.getImageData(0, 0, fw, fh);
        let minX = fw, minY = fh, maxX = -1, maxY = -1, r = 0, g = 0, b = 0, n = 0, opaque = 0;
        for (let y = 0; y < fh; y++) {
          for (let x = 0; x < fw; x++) {
            const a = data[(y * fw + x) * 4 + 3];
            if (a > 30) {
              opaque++;
              if (x < minX) minX = x;
              if (x > maxX) maxX = x;
              if (y < minY) minY = y;
              if (y > maxY) maxY = y;
              r += data[(y * fw + x) * 4];
              g += data[(y * fw + x) * 4 + 1];
              b += data[(y * fw + x) * 4 + 2];
              n++;
            }
          }
        }
        if (n) tint = ((Math.round(r / n) << 16) | (Math.round(g / n) << 8) | Math.round(b / n));
        if (maxX >= minX && opaque < fw * fh * 0.985) {
          x0 = minX; y0 = minY; x1 = maxX + 1; y1 = maxY + 1;
        }
      } catch {
        /* tainted — use full image */
      }
    }
    const out = document.createElement('canvas');
    out.width = x1 - x0;
    out.height = y1 - y0;
    out.getContext('2d')?.drawImage(img, x0, y0, x1 - x0, y1 - y0, 0, 0, x1 - x0, y1 - y0);
    return { canvas: out, tint };
  }

  function clearModel() {
    glassesGroup.visible = false;
    modelMaterial = null;
    transitionProgress = 1.0;
  }

  const v = (i: number, lms: NormLandmark[], aspect: number) => toWorld(lms[i], aspect);

  function update(det: RawDetection, videoW: number, videoH: number) {
    const lms = det.landmarks;
    const aspect = videoW / videoH;

    camera.left = -aspect;
    camera.right = aspect;
    camera.updateProjectionMatrix();
    bgMesh.scale.set(-aspect, 1, 1);
    bgCam.left = -aspect;
    bgCam.right = aspect;
    bgCam.updateProjectionMatrix();

    const mx = (p: number[]) => [-p[0], p[1], p[2]];

    const templeL = mx(v(L.templeL, lms, aspect));
    const templeR = mx(v(L.templeR, lms, aspect));
    const bridge = mx(v(L.noseBridge, lms, aspect));
    const eyeL = mx(v(L.eyeOuterL, lms, aspect));
    const eyeR = mx(v(L.eyeOuterR, lms, aspect));

    const faceWidth = dist3(templeL, templeR);

    // Advance the frame-swap transition (fade-in + elastic scale).
    if (transitionProgress < 1.0) {
      transitionProgress = Math.min(transitionProgress + 0.05, 1.0);
      if (modelMaterial) modelMaterial.opacity = transitionProgress * maxTargetOpacity;
    }

    if (modelRoot && glassesGroup.visible) {
      glassesGroup.position.set(bridge[0], bridge[1] - faceWidth * 0.06, bridge[2] + faceWidth * 0.06);

      const bounceScale = easeOutBack(transitionProgress);
      const s = ((faceWidth * 1.0) / modelBaseWidth) * bounceScale;
      glassesGroup.scale.set(s, s, s);

      const roll = Math.atan2(eyeR[1] - eyeL[1], eyeR[0] - eyeL[0]);

      let yaw = 0;
      if (det.matrix && det.matrix.length >= 16) {
        yaw = Math.atan2(-det.matrix[2], det.matrix[10]);
      }
      glassesGroup.rotation.set(0, -yaw * 0.5, roll);
    }

    // Occluder can wrongly clip a flat photo front, so disable it in photo mode.
    occluder.visible = !isPhotoFrame;

    const pos = occluderGeom.getAttribute('position') as import('three').BufferAttribute;
    L.faceOval.forEach((idx, i) => {
      const p = mx(v(idx, lms, aspect));
      pos.setXYZ(i, p[0], p[1], p[2] - faceWidth * 0.04);
    });
    pos.needsUpdate = true;
    occluderGeom.computeVertexNormals();

    render();
  }

  function render() {
    renderer.autoClear = true;
    renderer.render(bgScene, bgCam);
    renderer.autoClear = false;
    renderer.clearDepth();
    renderer.render(scene, camera);
  }

  function resize() {
    const w = video.videoWidth || 1280;
    const h = video.videoHeight || 720;
    renderer.setSize(w, h, false);
  }
  resize();

  function snapshot(): string {
    render();
    try {
      return renderer.domElement.toDataURL('image/png');
    } catch {
      return '';
    }
  }

  function dispose() {
    renderer.dispose();
    videoTex.dispose();
    occluderGeom.dispose();
  }

  return { setPhotoFrame, clearModel, update, snapshot, resize, dispose };
}
